import React, { useCallback, useEffect, useMemo, useState } from "react";
import Card from "../../ui/Card";
import Button from "../../ui/Button";
import Input from "../../ui/Input";
import { authFetch } from "../../../lib/authFetch";

type DriverAdminDetailModalProps = {
  open: boolean;
  token: string | null;
  driverId: number | null;
  userId?: number | null;
  username?: string;
  onClose: () => void;
  onUpdated?: () => Promise<void> | void;
};

type DriverResponse = {
  ok?: boolean;
  error?: string;
  driver?: Record<string, unknown>;
  drivers?: Array<Record<string, unknown>>;
  data?: Record<string, unknown> | Array<Record<string, unknown>>;
  result?: Record<string, unknown> | Array<Record<string, unknown>>;
};

type DriverExtraResponse = {
  ok?: boolean;
  error?: string;
  extra?: Record<string, unknown>;
  data?: Record<string, unknown> | Array<Record<string, unknown>>;
  result?: Record<string, unknown> | Array<Record<string, unknown>>;
};

type DeliveriesResponse = {
  ok?: boolean;
  error?: string;
  deliveries?: Array<Record<string, unknown>> | Record<string, unknown>;
  data?: Array<Record<string, unknown>> | Record<string, unknown>;
  result?: Array<Record<string, unknown>> | Record<string, unknown>;
};

type DevicesResponse = {
  ok?: boolean;
  error?: string;
  devices?: Array<Record<string, unknown>> | Record<string, unknown>;
  data?: Array<Record<string, unknown>> | Record<string, unknown>;
  result?: Array<Record<string, unknown>> | Record<string, unknown>;
};

type GenericResponse = {
  ok?: boolean;
  error?: string;
  message?: string;
};

type DriverGeneralForm = {
  tariffID: string;
  vehiculo: string;
  patente: string;
  DNI: string;
  CUIL: string;
  alias: string;
  CBU: string;
  comision: string;
};

type DriverExtraForm = {
  DNI_frente: string;
  DNI_dorso: string;
  cedula_frente: string;
  cedula_dorso: string;
  registro_frente: string;
  registro_dorso: string;
  selfie: string;
};

type PreviewDocument = {
  title: string;
  value: string;
};

const modalOverlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 120,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "16px",
};

const backdropStyle: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  background: "rgba(0,0,0,0.65)",
};

const shellStyle: React.CSSProperties = {
  position: "relative",
  width: "100%",
  maxWidth: "1200px",
  maxHeight: "88vh",
  overflow: "hidden",
};

const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  padding: "18px 20px",
  borderBottom: "1px solid var(--border)",
};

const bodyStyle: React.CSSProperties = {
  padding: "20px",
  maxHeight: "76vh",
  overflowY: "auto",
};

const gridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: "16px",
};

const tripleGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: "16px",
};

const documentsLayoutStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "260px minmax(0, 1fr)",
  gap: "18px",
  alignItems: "start",
};

const documentsGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: "14px",
};

const tabRowStyle: React.CSSProperties = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
  marginBottom: "20px",
};

const infoBoxStyle: React.CSSProperties = {
  border: "1px solid var(--border)",
  borderRadius: "16px",
  background: "rgba(255,255,255,0.03)",
  padding: "14px",
};

const mutedStyle: React.CSSProperties = {
  fontSize: "12px",
  color: "var(--text-secondary)",
};

const valueStyle: React.CSSProperties = {
  marginTop: "6px",
  fontSize: "14px",
  color: "var(--text-primary)",
};

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "18px",
  fontWeight: 700,
  color: "var(--text-primary)",
};

const selectStyle: React.CSSProperties = {
  width: "100%",
  minHeight: "48px",
  borderRadius: "16px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  color: "var(--text-primary)",
  padding: "0 14px",
  fontSize: "14px",
};

const tableWrapStyle: React.CSSProperties = {
  overflowX: "auto",
  border: "1px solid var(--border)",
  borderRadius: "18px",
  background: "rgba(255,255,255,0.02)",
};

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
};

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "12px",
  color: "var(--text-secondary)",
  fontSize: "13px",
  fontWeight: 700,
  borderBottom: "1px solid var(--border)",
};

const tdStyle: React.CSSProperties = {
  padding: "12px",
  color: "var(--text-primary)",
  fontSize: "14px",
  borderBottom: "1px solid var(--border)",
  verticalAlign: "top",
};

const documentCardStyle: React.CSSProperties = {
  border: "1px solid var(--border)",
  borderRadius: "18px",
  background: "rgba(255,255,255,0.03)",
  overflow: "hidden",
};

const documentCompactCardStyle: React.CSSProperties = {
  ...documentCardStyle,
  borderRadius: "16px",
};

const documentPreviewStyle: React.CSSProperties = {
  width: "100%",
  height: "180px",
  objectFit: "cover",
  display: "block",
  background: "rgba(255,255,255,0.04)",
  borderBottom: "1px solid var(--border)",
};

const documentCompactPreviewStyle: React.CSSProperties = {
  width: "100%",
  height: "112px",
  objectFit: "cover",
  objectPosition: "center",
  display: "block",
  background: "rgba(255,255,255,0.04)",
  borderBottom: "1px solid var(--border)",
};

const selfiePreviewStyle: React.CSSProperties = {
  width: "150px",
  height: "150px",
  borderRadius: "999px",
  objectFit: "cover",
  objectPosition: "center 38%",
  display: "block",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid var(--border)",
};

const documentBodyStyle: React.CSSProperties = {
  padding: "14px",
  display: "grid",
  gap: "10px",
};

const documentCompactBodyStyle: React.CSSProperties = {
  padding: "12px",
  display: "grid",
  gap: "9px",
};

const documentActionsStyle: React.CSSProperties = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
};

const linkButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "36px",
  padding: "0 12px",
  borderRadius: "999px",
  border: "1px solid var(--border)",
  color: "var(--text-primary)",
  textDecoration: "none",
  fontSize: "13px",
  fontWeight: 700,
  background: "rgba(255,255,255,0.04)",
};

const iconButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "32px",
  padding: "0 10px",
  borderRadius: "999px",
  border: "1px solid var(--border)",
  color: "var(--text-primary)",
  textDecoration: "none",
  fontSize: "12px",
  fontWeight: 700,
  background: "rgba(255,255,255,0.04)",
  cursor: "pointer",
};

const previewOverlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 160,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "18px",
};

const previewBackdropStyle: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  background: "rgba(0,0,0,0.82)",
};

const previewShellStyle: React.CSSProperties = {
  position: "relative",
  width: "100%",
  maxWidth: "920px",
  maxHeight: "88vh",
  borderRadius: "22px",
  border: "1px solid var(--border)",
  background: "var(--surface)",
  overflow: "hidden",
  boxShadow: "0 24px 80px rgba(0,0,0,0.45)",
};

const commissionOptions = [
  { label: "30%", value: "0.30" },
  { label: "25%", value: "0.25" },
  { label: "20%", value: "0.20" },
  { label: "15%", value: "0.15" },
  { label: "10%", value: "0.10" },
  { label: "5%", value: "0.05" },
];

function TabButton({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        minHeight: "42px",
        padding: "0 16px",
        borderRadius: "999px",
        border: active ? "1px solid var(--border)" : "1px solid transparent",
        background: active ? "var(--primary-soft)" : "transparent",
        color: active ? "var(--text-primary)" : "var(--text-secondary)",
        fontSize: "14px",
        fontWeight: active ? 700 : 600,
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        display: "block",
        marginBottom: "8px",
        fontSize: "12px",
        color: "var(--text-secondary)",
      }}
    >
      {children}
    </span>
  );
}

function SelectField({
  label,
  children,
  ...props
}: React.SelectHTMLAttributes<HTMLSelectElement> & { label: string }) {
  return (
    <label style={{ display: "block" }}>
      <FieldLabel>{label}</FieldLabel>
      <select {...props} style={selectStyle}>
        {children}
      </select>
    </label>
  );
}

function firstObject<T = Record<string, unknown>>(value: unknown): T | null {
  if (!value) return null;
  if (Array.isArray(value)) return (value[0] as T) || null;
  if (typeof value === "object") return value as T;
  return null;
}

function normalizeDriver(payload: DriverResponse): Record<string, unknown> | null {
  return (
    firstObject(payload.driver) ||
    firstObject(payload.drivers) ||
    firstObject(payload.data) ||
    firstObject(payload.result)
  );
}

function normalizeExtra(payload: DriverExtraResponse): Record<string, unknown> | null {
  return (
    firstObject(payload.extra) ||
    firstObject(payload.data) ||
    firstObject(payload.result)
  );
}

function normalizeArray(
  payload: Record<string, unknown> | null | undefined
): Record<string, unknown>[] {
  if (!payload) return [];

  const source =
    payload.deliveries ||
    payload.devices ||
    payload.data ||
    payload.result;

  if (Array.isArray(source)) return source as Record<string, unknown>[];
  if (source && typeof source === "object") return [source as Record<string, unknown>];
  return [];
}

function asString(value: unknown): string {
  if (value == null) return "";
  return String(value);
}

function firstString(...values: unknown[]): string {
  for (const value of values) {
    const parsed = asString(value).trim();
    if (parsed) return parsed;
  }

  return "";
}

function formatDate(value?: unknown): string {
  if (!value) return "—";
  const raw = String(value);
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleString("es-AR");
}

function isImageUrl(value: string): boolean {
  const clean = value.split("?")[0].toLowerCase();
  return (
    clean.endsWith(".jpg") ||
    clean.endsWith(".jpeg") ||
    clean.endsWith(".png") ||
    clean.endsWith(".webp")
  );
}

function EmptyDocumentPreview({
  compact,
  round,
  hasValue,
}: {
  compact?: boolean;
  round?: boolean;
  hasValue: boolean;
}) {
  return (
    <div
      style={{
        ...(compact ? documentCompactPreviewStyle : documentPreviewStyle),
        ...(round ? selfiePreviewStyle : {}),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "var(--text-secondary)",
        fontSize: "13px",
        textAlign: "center",
        padding: "12px",
      }}
    >
      {hasValue ? "Vista previa no disponible" : "Sin documento"}
    </div>
  );
}

function DocumentField({
  title,
  value,
  onChange,
  compact = false,
  selfie = false,
  onPreview,
}: {
  title: string;
  value: string;
  onChange: (value: string) => void;
  compact?: boolean;
  selfie?: boolean;
  onPreview?: (document: PreviewDocument) => void;
}) {
  const hasValue = Boolean(value.trim());
  const canPreview = hasValue && isImageUrl(value);

  if (selfie) {
    return (
      <div
        style={{
          ...infoBoxStyle,
          display: "grid",
          justifyItems: "center",
          gap: "14px",
          textAlign: "center",
        }}
      >
        <div>
          <h4
            style={{
              margin: 0,
              fontSize: "15px",
              fontWeight: 800,
              color: "var(--text-primary)",
            }}
          >
            Selfie del conductor
          </h4>
          <p
            style={{
              margin: "4px 0 0 0",
              color: "var(--text-secondary)",
              fontSize: "12px",
              lineHeight: 1.4,
            }}
          >
            Foto principal para validar identidad.
          </p>
        </div>

        {canPreview ? (
          <button
            type="button"
            onClick={() => onPreview?.({ title, value })}
            style={{
              padding: 0,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              borderRadius: "999px",
            }}
          >
            <img src={value} alt={title} style={selfiePreviewStyle} />
          </button>
        ) : (
          <EmptyDocumentPreview hasValue={hasValue} round />
        )}

        <div style={{ width: "100%" }}>
          <FieldLabel>{title}</FieldLabel>
          <Input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="URL o referencia"
          />
        </div>

        <div style={documentActionsStyle}>
          {canPreview ? (
            <button type="button" style={iconButtonStyle} onClick={() => onPreview?.({ title, value })}>
              Ampliar
            </button>
          ) : null}

          {hasValue ? (
            <a href={value} target="_blank" rel="noreferrer" style={iconButtonStyle}>
              Abrir
            </a>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div style={compact ? documentCompactCardStyle : documentCardStyle}>
      {canPreview ? (
        <button
          type="button"
          onClick={() => onPreview?.({ title, value })}
          style={{
            padding: 0,
            border: "none",
            background: "transparent",
            width: "100%",
            cursor: "pointer",
          }}
        >
          <img
            src={value}
            alt={title}
            style={compact ? documentCompactPreviewStyle : documentPreviewStyle}
          />
        </button>
      ) : (
        <EmptyDocumentPreview compact={compact} hasValue={hasValue} />
      )}

      <div style={compact ? documentCompactBodyStyle : documentBodyStyle}>
        <div>
          <FieldLabel>{title}</FieldLabel>
          <Input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="URL o referencia"
          />
        </div>

        <div style={documentActionsStyle}>
          {canPreview ? (
            <button type="button" style={iconButtonStyle} onClick={() => onPreview?.({ title, value })}>
              Ampliar
            </button>
          ) : null}

          {hasValue ? (
            <a href={value} target="_blank" rel="noreferrer" style={iconButtonStyle}>
              Abrir
            </a>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function DocumentPreviewModal({
  document,
  onClose,
}: {
  document: PreviewDocument | null;
  onClose: () => void;
}) {
  if (!document) return null;

  return (
    <div style={previewOverlayStyle}>
      <div style={previewBackdropStyle} onClick={onClose} />

      <div style={previewShellStyle}>
        <div style={headerStyle}>
          <div>
            <h3 style={{ ...sectionTitleStyle, fontSize: "20px" }}>{document.title}</h3>
            <p
              style={{
                margin: "6px 0 0 0",
                color: "var(--text-secondary)",
                fontSize: "13px",
              }}
            >
              Vista ampliada del documento.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--text-secondary)",
              fontSize: "22px",
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>

        <div
          style={{
            padding: "18px",
            maxHeight: "72vh",
            overflow: "auto",
            display: "flex",
            justifyContent: "center",
            background: "rgba(255,255,255,0.02)",
          }}
        >
          <img
            src={document.value}
            alt={document.title}
            style={{
              width: "100%",
              maxWidth: "820px",
              maxHeight: "68vh",
              objectFit: "contain",
              borderRadius: "16px",
              border: "1px solid var(--border)",
              background: "rgba(255,255,255,0.04)",
            }}
          />
        </div>

        <div
          style={{
            padding: "14px 18px",
            borderTop: "1px solid var(--border)",
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
          }}
        >
          <a href={document.value} target="_blank" rel="noreferrer" style={linkButtonStyle}>
            Abrir original
          </a>
          <Button variant="secondary" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function DriverAdminDetailModal({
  open,
  token,
  driverId,
  userId,
  username,
  onClose,
  onUpdated,
}: DriverAdminDetailModalProps) {
  const [tab, setTab] = useState<"GENERAL" | "DOCUMENTACION" | "ENVIOS" | "DEVICES" | "SEGURIDAD" | "LEGACY">(
    "GENERAL"
  );

  const [loading, setLoading] = useState(false);
  const [savingGeneral, setSavingGeneral] = useState(false);
  const [savingExtra, setSavingExtra] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [warehouseAccess, setWarehouseAccess] = useState(false);
  const [savingWarehouse, setSavingWarehouse] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const [driverRaw, setDriverRaw] = useState<Record<string, unknown> | null>(null);
  const [deliveries, setDeliveries] = useState<Record<string, unknown>[]>([]);
  const [devices, setDevices] = useState<Record<string, unknown>[]>([]);
  const [previewDocument, setPreviewDocument] = useState<PreviewDocument | null>(null);

  const [generalForm, setGeneralForm] = useState<DriverGeneralForm>({
    tariffID: "",
    vehiculo: "",
    patente: "",
    DNI: "",
    CUIL: "",
    alias: "",
    CBU: "",
    comision: "",
  });

  const [extraForm, setExtraForm] = useState<DriverExtraForm>({
    DNI_frente: "",
    DNI_dorso: "",
    cedula_frente: "",
    cedula_dorso: "",
    registro_frente: "",
    registro_dorso: "",
    selfie: "",
  });

  // Tab Legacy (ver informe "LYNX — Mejoras Frontend — Auditoría del ciclo
  // semanal", Parte 1): lightdata_username PRINCIPAL (una sola columna en
  // driver_data, editable acá por primera vez en este modal) + alias
  // ADICIONALES (driver_legacy_alias, tabla nueva) — nunca mezclado con
  // alias bancarios/CBU ni ningún otro concepto.
  const [lightdataUsername, setLightdataUsername] = useState("");
  const [savingLightdataUsername, setSavingLightdataUsername] = useState(false);
  const [legacyAliases, setLegacyAliases] = useState<{ id: number; lightdata_username: string; created_at: string }[]>([]);
  const [legacyLoading, setLegacyLoading] = useState(true);
  const [legacyErr, setLegacyErr] = useState<string | null>(null);
  const [newAliasValue, setNewAliasValue] = useState("");
  const [addingAlias, setAddingAlias] = useState(false);
  const [removingAliasId, setRemovingAliasId] = useState<number | null>(null);

  const [passwordForm, setPasswordForm] = useState({
    newPassword: "",
  });

  // Tarifas DRIVER para el selector de "Configuración logística" (Sprint
  // Operativo — Parte 2: la tarifa se administra ACÁ, no desde
  // Liquidaciones). Se piden una sola vez por apertura del modal.
  const [driverTariffs, setDriverTariffs] = useState<{ id: number; name: string }[]>([]);
  useEffect(() => {
    if (!open || !token) return;
    let cancelled = false;
    authFetch("/v2/tariffs", { token })
      .then(async (res) => {
        const payload = await res.json().catch(() => ({}));
        if (cancelled || !res.ok || payload?.ok === false) return;
        const all = Array.isArray(payload.tariffs) ? payload.tariffs : [];
        setDriverTariffs(all.filter((t: any) => t.type === "DRIVER").map((t: any) => ({ id: t.id, name: t.name })));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open, token]);

  // Vehículo solo SUGIERE una tarifa por defecto — nunca la fuerza (ver
  // informe "Sprint Operativo"). AUTO/MOTO/UTILITARIO -> tarifa del mismo
  // nombre, si existe y todavía no es la seleccionada.
  const suggestedTariff = useMemo(() => {
    const vehiculoNorm = generalForm.vehiculo.trim().toUpperCase();
    const tariffNameByVehiculo: Record<string, string> = { AUTO: "Auto", MOTO: "Moto", UTILITARIO: "Utilitario" };
    const suggestedName = tariffNameByVehiculo[vehiculoNorm];
    if (!suggestedName) return null;
    const match = driverTariffs.find((t) => t.name === suggestedName);
    if (!match || String(match.id) === generalForm.tariffID) return null;
    return match;
  }, [generalForm.vehiculo, generalForm.tariffID, driverTariffs]);

  const displayTitle = useMemo(() => {
    const alias = asString(driverRaw?.alias).trim();
    if (alias) return alias;
    if (username) return username;
    return driverId ? `Chofer #${driverId}` : "Detalle de chofer";
  }, [driverId, driverRaw, username]);

  const loadAll = useCallback(async () => {
    if (!open || !token || !driverId) return;

    setLoading(true);
    setError(null);

    try {
      const requests: Promise<Response>[] = [
        authFetch(`/v2/drivers/${driverId}`, { token }),
        authFetch(`/v2/drivers/${driverId}/extra`, { token }),
        authFetch(`/v2/drivers/${driverId}/deliveries`, { token }),
      ];

      if (userId) {
        requests.push(authFetch(`/v2/users/${userId}/devices`, { token }));
      }

      const responses = await Promise.all(requests);

      const driverRes = responses[0];
      const extraRes = responses[1];
      const deliveriesRes = responses[2];
      const devicesRes = responses[3];

      const driverJson: DriverResponse = await driverRes.json().catch(() => ({}));
      const extraJson: DriverExtraResponse = await extraRes.json().catch(() => ({}));
      const deliveriesJson: DeliveriesResponse = await deliveriesRes.json().catch(() => ({}));

      if (!driverRes.ok || driverJson?.ok === false) {
        throw new Error(driverJson?.error || "No se pudo cargar el driver");
      }

      const driver = normalizeDriver(driverJson);
      const extra = extraRes.ok && extraJson?.ok !== false ? normalizeExtra(extraJson) : null;
      const driverDeliveries = deliveriesRes.ok && deliveriesJson?.ok !== false
        ? normalizeArray(deliveriesJson)
        : [];

      let userDevices: Record<string, unknown>[] = [];
      if (devicesRes) {
        const devicesJson: DevicesResponse = await devicesRes.json().catch(() => ({}));
        if (devicesRes.ok && devicesJson?.ok !== false) {
          userDevices = normalizeArray(devicesJson);
        }
      }

      setDriverRaw(driver);
      setDeliveries(driverDeliveries);
      setDevices(userDevices);
      setWarehouseAccess(driver?.warehouse_access === true);
      setLightdataUsername(asString(driver?.lightdata_username));

      setGeneralForm({
        tariffID:
          asString(driver?.tarifaID ?? driver?.tariffID ?? driver?.tarifa_id ?? driver?.tariff_id) ||
          "",
        vehiculo: asString(driver?.vehiculo),
        patente: asString(driver?.patente),
        DNI: firstString(driver?.DNI, driver?.dni),
        CUIL: firstString(driver?.CUIL, driver?.cuil),
        alias: asString(driver?.alias),
        CBU: firstString(driver?.CBU, driver?.cbu),
        comision: asString(driver?.comision),
      });

      setExtraForm({
        DNI_frente: firstString(extra?.DNI_frente, extra?.dni_frente),
        DNI_dorso: firstString(extra?.DNI_dorso, extra?.dni_dorso),
        cedula_frente: firstString(extra?.cedula_frente, extra?.Cedula_frente),
        cedula_dorso: firstString(extra?.cedula_dorso, extra?.Cedula_dorso),
        registro_frente: firstString(extra?.registro_frente, extra?.Registro_frente),
        registro_dorso: firstString(extra?.registro_dorso, extra?.Registro_dorso),
        selfie: firstString(extra?.selfie, extra?.Selfie),
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error cargando detalle del chofer");
      setDriverRaw(null);
      setDeliveries([]);
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, [open, token, driverId, userId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleSaveGeneral = async () => {
    if (!token || !driverId) return;

    setSavingGeneral(true);
    setError(null);

    try {
      // DNI/CUIL son bigint en driver_data (ver informe "Sprint Final —
      // Cierre de Liquidaciones"): mandar "" en vez de null hace que TODO
      // el guardado falle con un error de sintaxis de Postgres — y con él,
      // la tarifa manual nunca llega a persistir. Ningún otro campo de este
      // formulario es numérico estricto, así que son los únicos que
      // necesitan este tratamiento.
      const payload = {
        driver: {
          tarifaId: generalForm.tariffID ? Number(generalForm.tariffID) : null,
          vehiculo: generalForm.vehiculo.trim(),
          patente: generalForm.patente.trim(),
          DNI: generalForm.DNI.trim() || null,
          CUIL: generalForm.CUIL.trim() || null,
          alias: generalForm.alias.trim(),
          CBU: generalForm.CBU.trim(),
          comision: Number(generalForm.comision || "0"),
        },
      };

      const response = await authFetch(`/v2/drivers/${driverId}`, {
        method: "PUT",
        token,
        json: true,
        body: JSON.stringify(payload),
      });

      const json: GenericResponse = await response.json().catch(() => ({}));

      if (!response.ok || json?.ok === false) {
        throw new Error(json?.error || "No se pudo guardar el driver");
      }

      if (generalForm.tariffID) {
        const tariffResponse = await authFetch(`/v2/drivers/${driverId}/tariff`, {
          method: "PUT",
          token,
          json: true,
          body: JSON.stringify({ tarifaID: Number(generalForm.tariffID) }),
        });

        const tariffJson: GenericResponse = await tariffResponse.json().catch(() => ({}));
        if (!tariffResponse.ok || tariffJson?.ok === false) {
          throw new Error(tariffJson?.error || "No se pudo actualizar la tarifa");
        }
      }

      await loadAll();
      await onUpdated?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error guardando datos generales");
    } finally {
      setSavingGeneral(false);
    }
  };

  const handleSaveExtra = async () => {
    if (!token || !driverId) return;

    setSavingExtra(true);
    setError(null);

    try {
      const payload = {
        driver: {
          DNI_frente: extraForm.DNI_frente.trim(),
          DNI_dorso: extraForm.DNI_dorso.trim(),
          cedula_frente: extraForm.cedula_frente.trim(),
          cedula_dorso: extraForm.cedula_dorso.trim(),
          registro_frente: extraForm.registro_frente.trim(),
          registro_dorso: extraForm.registro_dorso.trim(),
          selfie: extraForm.selfie.trim(),
        },
      };

      const response = await authFetch(`/v2/drivers/${driverId}/extra`, {
        method: "PUT",
        token,
        json: true,
        body: JSON.stringify(payload),
      });

      const json: GenericResponse = await response.json().catch(() => ({}));

      if (!response.ok || json?.ok === false) {
        throw new Error(json?.error || "No se pudo guardar la documentación");
      }

      await loadAll();
      await onUpdated?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error guardando documentación");
    } finally {
      setSavingExtra(false);
    }
  };

  // El acceso al depósito es la capacidad que le abre la Recepción en planta
  // desde la app. Va por su propia ruta: PUT /v2/drivers/:id lo puede llamar el
  // propio chofer, y nadie se autoasigna una capacidad.
  const handleSaveWarehouseAccess = async (granted: boolean) => {
    if (!driverId) return;
    setSavingWarehouse(true);
    setError(null);
    try {
      const response = await authFetch(`/v2/drivers/${driverId}/warehouse-access`, {
        token,
        method: "PUT",
        json: true,
        body: JSON.stringify({ warehouse_access: granted }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok || json?.ok === false) {
        throw new Error(json?.error || "No se pudo cambiar el acceso al depósito");
      }
      setWarehouseAccess(granted);
      await onUpdated?.();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSavingWarehouse(false);
    }
  };

  const handleChangePassword = async () => {
    if (!token || !userId) return;
    if (!passwordForm.newPassword.trim()) {
      alert("Completá la nueva contraseña.");
      return;
    }

    setSavingPassword(true);
    setError(null);

    try {
      const response = await authFetch(`/v2/users/${userId}/change-password`, {
        method: "PUT",
        token,
        json: true,
        body: JSON.stringify({
          password: passwordForm.newPassword,
        }),
      });

      const json: GenericResponse = await response.json().catch(() => ({}));

      if (!response.ok || json?.ok === false) {
        throw new Error(json?.error || "No se pudo cambiar la contraseña");
      }

      setPasswordForm({ newPassword: "" });
      alert("Contraseña actualizada correctamente.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error cambiando contraseña");
    } finally {
      setSavingPassword(false);
    }
  };

  const saveLightdataUsername = async () => {
    if (!driverId) return;
    const value = lightdataUsername.trim();
    setSavingLightdataUsername(true);
    setError(null);
    try {
      const response = await authFetch(`/v2/drivers/${driverId}/lightdata-username`, {
        method: "PUT",
        token,
        json: true,
        body: JSON.stringify({ lightdata_username: value || null }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok || json?.ok === false) {
        throw new Error(json?.error || "No se pudo actualizar el lightdata_username");
      }
      await onUpdated?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error actualizando lightdata_username");
    } finally {
      setSavingLightdataUsername(false);
    }
  };

  const loadLegacyAliases = useCallback(async () => {
    if (!driverId || !token) return;
    setLegacyLoading(true);
    setLegacyErr(null);
    try {
      const r = await authFetch(`/v2/drivers/${driverId}/aliases`, { token });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j?.ok === false) {
        throw new Error(j?.error || "No se pudieron cargar los alias Legacy");
      }
      setLegacyAliases(j.aliases || []);
    } catch (e) {
      setLegacyErr(e instanceof Error ? e.message : "Error cargando alias Legacy");
      setLegacyAliases([]);
    } finally {
      setLegacyLoading(false);
    }
  }, [driverId, token]);

  useEffect(() => {
    if (open) loadLegacyAliases();
  }, [open, loadLegacyAliases]);

  const addLegacyAlias = async () => {
    const value = newAliasValue.trim();
    if (!value || !driverId) return;
    setAddingAlias(true);
    setLegacyErr(null);
    try {
      const r = await authFetch(`/v2/drivers/${driverId}/aliases`, {
        method: "POST",
        token,
        json: true,
        body: JSON.stringify({ lightdata_username: value }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j?.ok === false) {
        throw new Error(j?.error || "No se pudo agregar el alias");
      }
      setNewAliasValue("");
      await loadLegacyAliases();
    } catch (e) {
      setLegacyErr(e instanceof Error ? e.message : "Error agregando el alias");
    } finally {
      setAddingAlias(false);
    }
  };

  const removeLegacyAlias = async (aliasId: number, aliasName: string) => {
    if (!driverId) return;
    if (!window.confirm(`¿Eliminar el alias Legacy "${aliasName}"? Los envíos con ese cadete van a dejar de resolverse a este Driver hasta que se vuelva a vincular.`)) return;
    setRemovingAliasId(aliasId);
    setLegacyErr(null);
    try {
      const r = await authFetch(`/v2/drivers/${driverId}/aliases/${aliasId}`, { method: "DELETE", token });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j?.ok === false) {
        throw new Error(j?.error || "No se pudo eliminar el alias");
      }
      await loadLegacyAliases();
    } catch (e) {
      setLegacyErr(e instanceof Error ? e.message : "Error eliminando el alias");
    } finally {
      setRemovingAliasId(null);
    }
  };

  if (!open) return null;

  return (
    <div style={modalOverlayStyle}>
      <div style={backdropStyle} onClick={onClose} />

      <div style={shellStyle}>
        <Card style={{ padding: 0 }}>
          <div style={headerStyle}>
            <div>
              <h2
                style={{
                  margin: 0,
                  fontSize: "28px",
                  fontWeight: 800,
                  color: "var(--text-primary)",
                }}
              >
                {displayTitle}
              </h2>
              <p
                style={{
                  margin: "6px 0 0 0",
                  fontSize: "14px",
                  color: "var(--text-secondary)",
                }}
              >
                Administración completa del chofer desde el panel admin.
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              style={{
                background: "transparent",
                border: "none",
                color: "var(--text-secondary)",
                fontSize: "22px",
                cursor: "pointer",
              }}
            >
              ×
            </button>
          </div>

          <div style={bodyStyle}>
            <div style={tabRowStyle}>
              <TabButton active={tab === "GENERAL"} onClick={() => setTab("GENERAL")}>
                General
              </TabButton>
              <TabButton
                active={tab === "DOCUMENTACION"}
                onClick={() => setTab("DOCUMENTACION")}
              >
                Documentación
              </TabButton>
              <TabButton active={tab === "ENVIOS"} onClick={() => setTab("ENVIOS")}>
                Envíos
              </TabButton>
              <TabButton active={tab === "DEVICES"} onClick={() => setTab("DEVICES")}>
                Devices
              </TabButton>
              <TabButton active={tab === "SEGURIDAD"} onClick={() => setTab("SEGURIDAD")}>
                Seguridad
              </TabButton>
              <TabButton active={tab === "LEGACY"} onClick={() => setTab("LEGACY")}>
                Legacy
              </TabButton>
            </div>

            {error ? (
              <div style={{ marginBottom: "16px", color: "var(--danger)", fontSize: "14px" }}>
                {error}
              </div>
            ) : null}

            {loading ? (
              <div style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
                Cargando detalle del chofer...
              </div>
            ) : null}

            {!loading && tab === "GENERAL" ? (
              <div style={{ display: "grid", gap: "20px" }}>
                <div style={tripleGridStyle} className="driver-admin-summary-grid">
                  <div style={infoBoxStyle}>
                    <div style={mutedStyle}>Driver ID</div>
                    <div style={valueStyle}>{driverId ?? "—"}</div>
                  </div>

                  <div style={infoBoxStyle}>
                    <div style={mutedStyle}>User ID</div>
                    <div style={valueStyle}>{userId ?? "—"}</div>
                  </div>

                  <div style={infoBoxStyle}>
                    <div style={mutedStyle}>Username</div>
                    <div style={valueStyle}>{username || "—"}</div>
                  </div>
                </div>

                <Card>
                  <div style={{ marginBottom: "16px" }}>
                    <h3 style={sectionTitleStyle}>Configuración logística</h3>
                    <p style={{ margin: "6px 0 0 0", color: "var(--text-secondary)", fontSize: "13px" }}>
                      El vehículo es el recurso operativo; la tarifa es la decisión comercial — no
                      son lo mismo, y la mayoría de las veces coinciden pero pueden diferir.
                    </p>
                  </div>

                  <div style={gridStyle} className="driver-admin-grid">
                    <div>
                      <FieldLabel>Vehículo</FieldLabel>
                      <Input
                        value={generalForm.vehiculo}
                        onChange={(e) =>
                          setGeneralForm((prev) => ({ ...prev, vehiculo: e.target.value }))
                        }
                        placeholder="Moto / Auto / Utilitario"
                      />
                    </div>

                    <div>
                      <SelectField
                        label="Tarifa aplicada"
                        value={generalForm.tariffID}
                        onChange={(e) =>
                          setGeneralForm((prev) => ({ ...prev, tariffID: e.target.value }))
                        }
                      >
                        <option value="">Sin tarifa</option>
                        {driverTariffs.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                      </SelectField>
                      {suggestedTariff && (
                        <button
                          type="button"
                          onClick={() => setGeneralForm((prev) => ({ ...prev, tariffID: String(suggestedTariff.id) }))}
                          style={{
                            marginTop: "8px",
                            fontSize: "12px",
                            color: "var(--primary)",
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            padding: 0,
                            textAlign: "left",
                          }}
                        >
                          Sugerencia: tarifa {suggestedTariff.name} (según el vehículo) — click para aplicar
                        </button>
                      )}
                    </div>
                  </div>
                </Card>

                <Card>
                  <div style={{ marginBottom: "16px" }}>
                    <h3 style={sectionTitleStyle}>Datos generales</h3>
                  </div>

                  <div style={gridStyle} className="driver-admin-grid">
                    <div>
                      <FieldLabel>Patente</FieldLabel>
                      <Input
                        value={generalForm.patente}
                        onChange={(e) =>
                          setGeneralForm((prev) => ({ ...prev, patente: e.target.value }))
                        }
                        placeholder="A123BCD"
                      />
                    </div>

                    <div>
                      <FieldLabel>DNI</FieldLabel>
                      <Input
                        value={generalForm.DNI}
                        onChange={(e) =>
                          setGeneralForm((prev) => ({ ...prev, DNI: e.target.value }))
                        }
                        placeholder="30111222"
                      />
                    </div>

                    <div>
                      <FieldLabel>CUIL</FieldLabel>
                      <Input
                        value={generalForm.CUIL}
                        onChange={(e) =>
                          setGeneralForm((prev) => ({ ...prev, CUIL: e.target.value }))
                        }
                        placeholder="20-30111222-3"
                      />
                    </div>

                    <div>
                      <FieldLabel>Alias</FieldLabel>
                      <Input
                        value={generalForm.alias}
                        onChange={(e) =>
                          setGeneralForm((prev) => ({ ...prev, alias: e.target.value }))
                        }
                        placeholder="driver.alias"
                      />
                    </div>

                    <div>
                      <FieldLabel>CBU</FieldLabel>
                      <Input
                        value={generalForm.CBU}
                        onChange={(e) =>
                          setGeneralForm((prev) => ({ ...prev, CBU: e.target.value }))
                        }
                        placeholder="2850590940090418135201"
                      />
                    </div>

                    <div>
                      <FieldLabel>Comisión (decimal)</FieldLabel>
                      <Input
                        value={generalForm.comision}
                        onChange={(e) =>
                          setGeneralForm((prev) => ({ ...prev, comision: e.target.value }))
                        }
                        placeholder="0.25"
                      />
                    </div>

                    <div style={{ gridColumn: "1 / -1" }}>
                      <FieldLabel>Comisión rápida</FieldLabel>
                      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                        {commissionOptions.map((option) => (
                          <Button
                            key={option.value}
                            type="button"
                            variant={
                              generalForm.comision === option.value ? "primary" : "secondary"
                            }
                            onClick={() =>
                              setGeneralForm((prev) => ({ ...prev, comision: option.value }))
                            }
                          >
                            {option.label}
                          </Button>
                        ))}
                      </div>
                      <div style={{ ...mutedStyle, marginTop: "10px" }}>
                        Se envía en formato decimal. Ejemplo: 25% → 0.25
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      marginTop: "24px",
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "12px",
                      flexWrap: "wrap",
                    }}
                  >
                    <Button variant="secondary" onClick={loadAll} disabled={savingGeneral}>
                      Recargar
                    </Button>
                    <Button onClick={handleSaveGeneral} disabled={savingGeneral}>
                      {savingGeneral ? "Guardando..." : "Guardar datos"}
                    </Button>
                  </div>
                </Card>
              </div>
            ) : null}

            {!loading && tab === "DOCUMENTACION" ? (
              <Card>
                <div style={{ marginBottom: "18px" }}>
                  <h3 style={sectionTitleStyle}>Documentación / driver_extra</h3>
                  <p style={{ margin: "6px 0 0 0", color: "var(--text-secondary)", fontSize: "14px" }}>
                    Archivos cargados desde mobile en Firebase y persistidos como URLs en backend.
                  </p>
                </div>

                <div style={documentsLayoutStyle} className="driver-documents-layout">
                  <DocumentField
                    title="Selfie"
                    value={extraForm.selfie}
                    selfie
                    onPreview={setPreviewDocument}
                    onChange={(value) =>
                      setExtraForm((prev) => ({ ...prev, selfie: value }))
                    }
                  />

                  <div style={documentsGridStyle} className="driver-documents-grid">
                    <DocumentField
                      title="DNI frente"
                      value={extraForm.DNI_frente}
                      compact
                      onPreview={setPreviewDocument}
                      onChange={(value) =>
                        setExtraForm((prev) => ({ ...prev, DNI_frente: value }))
                      }
                    />

                    <DocumentField
                      title="DNI dorso"
                      value={extraForm.DNI_dorso}
                      compact
                      onPreview={setPreviewDocument}
                      onChange={(value) =>
                        setExtraForm((prev) => ({ ...prev, DNI_dorso: value }))
                      }
                    />

                    <DocumentField
                      title="Cédula frente"
                      value={extraForm.cedula_frente}
                      compact
                      onPreview={setPreviewDocument}
                      onChange={(value) =>
                        setExtraForm((prev) => ({ ...prev, cedula_frente: value }))
                      }
                    />

                    <DocumentField
                      title="Cédula dorso"
                      value={extraForm.cedula_dorso}
                      compact
                      onPreview={setPreviewDocument}
                      onChange={(value) =>
                        setExtraForm((prev) => ({ ...prev, cedula_dorso: value }))
                      }
                    />

                    <DocumentField
                      title="Registro frente"
                      value={extraForm.registro_frente}
                      compact
                      onPreview={setPreviewDocument}
                      onChange={(value) =>
                        setExtraForm((prev) => ({ ...prev, registro_frente: value }))
                      }
                    />

                    <DocumentField
                      title="Registro dorso"
                      value={extraForm.registro_dorso}
                      compact
                      onPreview={setPreviewDocument}
                      onChange={(value) =>
                        setExtraForm((prev) => ({ ...prev, registro_dorso: value }))
                      }
                    />
                  </div>
                </div>

                <div
                  style={{
                    marginTop: "24px",
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "12px",
                    flexWrap: "wrap",
                  }}
                >
                  <Button variant="secondary" onClick={loadAll} disabled={savingExtra}>
                    Recargar
                  </Button>
                  <Button onClick={handleSaveExtra} disabled={savingExtra}>
                    {savingExtra ? "Guardando..." : "Guardar documentación"}
                  </Button>
                </div>
              </Card>
            ) : null}

            {!loading && tab === "ENVIOS" ? (
              <Card>
                <div style={{ marginBottom: "16px" }}>
                  <h3 style={sectionTitleStyle}>Envíos del chofer</h3>
                </div>

                {deliveries.length === 0 ? (
                  <div style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
                    Sin envíos cargados o el endpoint no devolvió resultados.
                  </div>
                ) : (
                  <div style={tableWrapStyle}>
                    <table style={tableStyle}>
                      <thead>
                        <tr>
                          <th style={thStyle}>ID</th>
                          <th style={thStyle}>Tracking</th>
                          <th style={thStyle}>Estado</th>
                          <th style={thStyle}>Dirección</th>
                          <th style={thStyle}>Actualizado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deliveries.map((delivery, index) => (
                          <tr key={`${delivery.id || index}`}>
                            <td style={tdStyle}>{asString(delivery.id) || "—"}</td>
                            <td style={tdStyle}>
                              {asString(delivery.tracking || delivery.numero_tracking) || "—"}
                            </td>
                            <td style={tdStyle}>
                              {asString(delivery.status || delivery.estado || delivery.status_ml) || "—"}
                            </td>
                            <td style={tdStyle}>
                              {asString(delivery.direccion || delivery.address || delivery.localidad) || "—"}
                            </td>
                            <td style={tdStyle}>
                              {formatDate(delivery.updated_at || delivery.fecha_estado)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            ) : null}

            {!loading && tab === "DEVICES" ? (
              <Card>
                <div style={{ marginBottom: "16px" }}>
                  <h3 style={sectionTitleStyle}>Devices del usuario</h3>
                </div>

                {!userId ? (
                  <div style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
                    Este modal no recibió `userId`, por eso no puede cargar devices.
                  </div>
                ) : devices.length === 0 ? (
                  <div style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
                    Sin devices asociados o sin datos disponibles todavía.
                  </div>
                ) : (
                  <div style={{ display: "grid", gap: "14px" }}>
                    {devices.map((device, index) => (
                      <div key={`${device.id || index}`} style={infoBoxStyle}>
                        <div style={gridStyle} className="driver-admin-grid">
                          <div>
                            <div style={mutedStyle}>Device ID</div>
                            <div style={valueStyle}>{asString(device.id) || "—"}</div>
                          </div>
                          <div>
                            <div style={mutedStyle}>Token</div>
                            <div style={valueStyle}>
                              {asString(device.token || device.device_token) || "—"}
                            </div>
                          </div>
                          <div>
                            <div style={mutedStyle}>Batería</div>
                            <div style={valueStyle}>
                              {asString(device.battery || device.battery_level) || "—"}
                            </div>
                          </div>
                          <div>
                            <div style={mutedStyle}>Estado</div>
                            <div style={valueStyle}>
                              {asString(device.active ?? device.estado ?? device.status) || "—"}
                            </div>
                          </div>
                          <div>
                            <div style={mutedStyle}>Latitud</div>
                            <div style={valueStyle}>
                              {asString(device.latitude || device.lat) || "—"}
                            </div>
                          </div>
                          <div>
                            <div style={mutedStyle}>Longitud</div>
                            <div style={valueStyle}>
                              {asString(device.longitude || device.lng) || "—"}
                            </div>
                          </div>
                          <div style={{ gridColumn: "1 / -1" }}>
                            <div style={mutedStyle}>Última actualización</div>
                            <div style={valueStyle}>
                              {formatDate(device.updated_at || device.last_seen || device.last_update)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            ) : null}

            {!loading && tab === "SEGURIDAD" ? (
              <Card>
                <div style={{ marginBottom: "16px" }}>
                  <h3 style={sectionTitleStyle}>Acceso al depósito</h3>
                  <p style={{ margin: "6px 0 0 0", color: "var(--text-secondary)", fontSize: "13px", lineHeight: 1.6 }}>
                    Le habilita la Recepción en planta desde la app: escanear el bulto,
                    verlo estanteado y moverlo de estantería. No toca liquidaciones ni usuarios.
                  </p>
                </div>

                <label style={{ display: "flex", alignItems: "center", gap: "10px", color: "var(--text-primary)", marginBottom: "24px" }}>
                  <input
                    type="checkbox"
                    checked={warehouseAccess}
                    disabled={savingWarehouse || !driverId}
                    onChange={(e) => handleSaveWarehouseAccess(e.target.checked)}
                  />
                  {savingWarehouse ? "Guardando…" : "Puede operar el depósito"}
                </label>

                <div style={{ marginBottom: "16px" }}>
                  <h3 style={sectionTitleStyle}>Seguridad / contraseña</h3>
                </div>

                {!userId ? (
                  <div style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
                    Este modal no recibió `userId`, por eso no puede cambiar contraseña.
                  </div>
                ) : (
                  <div style={gridStyle} className="driver-admin-grid">
                    <div>
                      <FieldLabel>Username</FieldLabel>
                      <Input value={username || ""} disabled />
                    </div>

                    <div>
                      <FieldLabel>Nueva contraseña</FieldLabel>
                      <Input
                        type="password"
                        value={passwordForm.newPassword}
                        onChange={(e) =>
                          setPasswordForm({ newPassword: e.target.value })
                        }
                        placeholder="Ingresá una nueva contraseña"
                      />
                    </div>
                  </div>
                )}

                {userId ? (
                  <div
                    style={{
                      marginTop: "24px",
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "12px",
                      flexWrap: "wrap",
                    }}
                  >
                    <Button onClick={handleChangePassword} disabled={savingPassword}>
                      {savingPassword ? "Guardando..." : "Cambiar contraseña"}
                    </Button>
                  </div>
                ) : null}
              </Card>
            ) : null}

            {!loading && tab === "LEGACY" ? (
              <Card>
                <div style={{ marginBottom: "16px" }}>
                  <h3 style={sectionTitleStyle}>lightdata_username</h3>
                  <p style={{ margin: "6px 0 0 0", color: "var(--text-secondary)", fontSize: "13px", lineHeight: 1.6 }}>
                    El cadete PRINCIPAL de LightData para este Driver. Editable acá — hasta ahora
                    solo se podía cambiar por API directa.
                  </p>
                </div>
                <div style={gridStyle} className="driver-admin-grid">
                  <div>
                    <FieldLabel>lightdata_username</FieldLabel>
                    <Input
                      value={lightdataUsername}
                      onChange={(e) => setLightdataUsername(e.target.value)}
                      placeholder="ej. rodrigogenero"
                    />
                  </div>
                </div>
                <div style={{ marginTop: "16px", display: "flex", justifyContent: "flex-end" }}>
                  <Button onClick={saveLightdataUsername} disabled={savingLightdataUsername}>
                    {savingLightdataUsername ? "Guardando..." : "Guardar lightdata_username"}
                  </Button>
                </div>

                <div style={{ marginTop: "28px", marginBottom: "16px" }}>
                  <h3 style={sectionTitleStyle}>Alias Legacy</h3>
                  <p style={{ margin: "6px 0 0 0", color: "var(--text-secondary)", fontSize: "13px", lineHeight: 1.6 }}>
                    Cadetes ADICIONALES que también resuelven a este Driver (ej. si el principal es
                    "ramirez", un alias puede ser "marceloramirez"). Exclusivamente para matching
                    Legacy — nunca se mezcla con alias bancarios ni ningún otro concepto.
                  </p>
                </div>

                {legacyErr ? (
                  <div style={{ marginBottom: "14px", color: "var(--danger)", fontSize: "14px" }}>{legacyErr}</div>
                ) : null}

                <div style={{ display: "flex", gap: "10px", marginBottom: "18px", flexWrap: "wrap" }}>
                  <div style={{ flex: "1 1 260px" }}>
                    <Input
                      placeholder='Nuevo alias, ej. "marceloramirez"'
                      value={newAliasValue}
                      onChange={(e) => setNewAliasValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !addingAlias) addLegacyAlias();
                      }}
                    />
                  </div>
                  <Button onClick={addLegacyAlias} disabled={addingAlias || !newAliasValue.trim()}>
                    {addingAlias ? "Agregando…" : "Agregar alias"}
                  </Button>
                </div>

                {legacyLoading ? (
                  <div style={{ color: "var(--text-secondary)", fontSize: "14px" }}>Cargando alias…</div>
                ) : legacyAliases.length === 0 ? (
                  <div style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
                    Sin alias Legacy todavía.
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {legacyAliases.map((a) => (
                      <div
                        key={a.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "12px",
                          padding: "10px 14px",
                          border: "1px solid var(--border)",
                          borderRadius: "12px",
                          background: "rgba(255,255,255,0.03)",
                        }}
                      >
                        <span style={{ fontFamily: "monospace", fontSize: "13px" }}>{a.lightdata_username}</span>
                        <button
                          type="button"
                          onClick={() => removeLegacyAlias(a.id, a.lightdata_username)}
                          disabled={removingAliasId === a.id}
                          style={{
                            background: "transparent",
                            border: "1px solid var(--border)",
                            borderRadius: "8px",
                            padding: "4px 10px",
                            color: "var(--danger, #d1242f)",
                            cursor: "pointer",
                            fontSize: "12px",
                          }}
                        >
                          {removingAliasId === a.id ? "Eliminando…" : "Eliminar"}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            ) : null}

            <style>
              {`
                @media (max-width: 1040px) {
                  .driver-documents-grid {
                    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
                  }
                }

                @media (max-width: 920px) {
                  .driver-admin-grid,
                  .driver-admin-summary-grid,
                  .driver-documents-layout {
                    grid-template-columns: 1fr !important;
                  }

                  .driver-documents-grid {
                    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
                  }
                }

                @media (max-width: 620px) {
                  .driver-documents-grid {
                    grid-template-columns: 1fr !important;
                  }
                }
              `}
            </style>
          </div>
        </Card>
      </div>

      <DocumentPreviewModal
        document={previewDocument}
        onClose={() => setPreviewDocument(null)}
      />
    </div>
  );
}